import { isSameDay, isSameMonth } from '@internationalized/date';
import { useCalendarCell } from '@react-aria/calendar';
import cn from 'classnames';
import { forwardRef, useRef } from 'react';

import { convertDateToAriaDateTime } from '../../../utils';
import { Button } from '../../buttons';

import type { CalendarDate } from '@internationalized/date';
import type { ForwardedRef, ReactElement } from 'react';

import type { CalendarCellProps } from './calendar-cell.types';

import './calendar-cell.scss';

const isToday = (date: CalendarDate): boolean => date.compare(convertDateToAriaDateTime(new Date())) === 0;

/**
 * The component is supposed to be used as a child of the `CalendarGrid` component.
 * @internal
 */
const CalendarCell = forwardRef(
  (props: CalendarCellProps, forwardedRef: ForwardedRef<HTMLLIElement> | null): ReactElement => {
    const {
      className,
      currentMonth: _currentMonth,
      date,
      state,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    const currentMonth = _currentMonth || date;

    const ref = useRef(null);

    const {
      buttonProps,
      cellProps,
      formattedDate,
      isDisabled: disabled,
      isFocused: hasFocus,
      isOutsideVisibleRange,
      isSelected: selected,
    } = useCalendarCell({ date }, state, ref);

    const isRangeCalendarState = 'highlightedRange' in state;
    let rangeCalendarClasses = {};
    if (isRangeCalendarState && state.highlightedRange) {
      const startDate = state.highlightedRange.start;
      const endDate = state.highlightedRange.end;

      const isSelectedRangeStart = isSameDay(startDate, date);
      const isSelectedRangeEnd = isSameDay(endDate, date);
      const isSelectedRange = startDate.compare(date) <= 0 && endDate.compare(date) >= 0;

      rangeCalendarClasses = cn({
        'rbui-calendar-cell--selected-range': isSelectedRange,
        'rbui-calendar-cell--selected-range-end': isSelectedRangeEnd,
        'rbui-calendar-cell--selected-range-start': isSelectedRangeStart,
      });
    }

    const classes = cn(
      'rbui-calendar-cell',
      {
        'rbui-calendar-cell--disabled': disabled,
        'rbui-calendar-cell--focus-visible': hasFocus,
        'rbui-calendar-cell--today': isToday(date),
      },
      isRangeCalendarState
        ? rangeCalendarClasses
        : {
            'rbui-calendar-cell--selected': selected,
          },
    );

    const isOutsideMonth = !isSameMonth(currentMonth, date);

    return (
      <li
        {...cellProps}
        className={cn(cellProps.className, 'rbui-calendar-cell__container', className)}
        ref={forwardedRef}
        {...rest}
      >
        {!(isOutsideVisibleRange || isOutsideMonth) && (
          <Button
            {...buttonProps}
            aria-selected={selected}
            className={classes}
            disabled={disabled}
            onClick={(event) => {
              if (event.pageX === 0 && event.pageY === 0) {
                return;
              }

              buttonProps.onClick?.(event);
            }}
            ref={ref}
            type="inline"
          >
            {formattedDate}
          </Button>
        )}
      </li>
    );
  },
);

CalendarCell.displayName = 'CalendarCell';

export default CalendarCell;
